import type { AxiosInstance } from 'axios';
import authService from './auth.service';

/**
 * Deposit notices (owner, 2026-10-31): the client tells us "I sent X on day D from my
 * registered account A for this series", or the Account Executive of their roster records it
 * for them. A notice moves no money: it is a hint for operations and the evidence of the rule
 * that matches a transfer from that account, for exactly that amount, inside a few business days.
 *
 *   POST /api/v1/clients/{client_id}/deposit-notices   the client, or the agent of their roster
 *   GET  /api/v1/clients/{client_id}/deposit-notices   the client's latest notices
 */

/** open: we are looking for it · matched: it arrived · past: its days ended with no match. */
export type DepositNoticeStatus = 'open' | 'matched' | 'past';

export interface DepositNotice {
  id: string;
  product_id: string;
  product_name: string;
  client_bank_account_id: string;
  amount: string;
  currency_code: string;
  /** The day the client says the transfer left (YYYY-MM-DD). */
  sent_on: string;
  recorded_at: string;
  /** False when the Account Executive recorded it for the client. */
  recorded_by_client: boolean;
  status: DepositNoticeStatus;
}

export interface DepositNoticePayload {
  product_id: string;
  client_bank_account_id: string;
  amount: string;
  currency_code: string;
  sent_on: string;
}

class DepositNoticesService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async list(clientId: string): Promise<DepositNotice[]> {
    const res = await this.api.get<DepositNotice[]>(`/api/v1/clients/${clientId}/deposit-notices`);
    return res.data;
  }

  async record(clientId: string, body: DepositNoticePayload): Promise<DepositNotice> {
    const res = await this.api.post<DepositNotice>(
      `/api/v1/clients/${clientId}/deposit-notices`,
      body,
    );
    return res.data;
  }
}

export default new DepositNoticesService();
